import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";

import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import Checkbox from "components/forms/Checkbox";
import { CheckMarkIcon, DownloadIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { mnt } from "theme/maintenanceTokens";
import { CorrigoField, CorrigoLineCard } from "components/CorrigoPasteField";
import { useToast } from "components/toast";

import { getCorrigoExport, markPostedToCorrigo, getInvoicePDF, getCorrigoProgress, saveCorrigoProgress } from "api/Invoices";

/**
 * §3.9 #18 — Corrigo Invoice submission panel. Design reference:
 * mockup 22 (22-corrigo-invoice-submission.svg). Mirrors
 * Quotes/components/CorrigoExportPanel.tsx (§3.9 #17) structurally,
 * adapted for the Invoice model and Corrigo's Invoice form:
 *
 *   - Same line-by-line paste cards (Category / Description / Qty /
 *     Rate) as the Quote panel, sourced from invoice line items instead
 *     of quote line items.
 *   - New: WO Completion Details section (Repair Category, Repair
 *     Code, Work Done Description) — editable text, pre-filled from
 *     plan defaults + an auto-composed draft description. Each field
 *     has its own Copy button, matching Corrigo's Invoice form.
 *   - New: Attachments checklist (invoice PDF + completion photos) —
 *     reminders only, same non-duplicative approach as the Quote
 *     panel's PDF-attach step (Overview tab already has the real
 *     download/email actions).
 *   - "Mark posted" replaces "Accept on behalf" — this is audit-only
 *     (see CorrigoInvoiceSubmission entity doc backend-side); there's
 *     no quote-style state transition to trigger first, so no gating
 *     checkbox sequence is needed before it's available.
 */
export default function CorrigoInvoiceSubmissionPanel({ invoice }: { invoice: any }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState<any>(null);
  const [repairCategory, setRepairCategory] = useState("");
  const [repairCode, setRepairCode] = useState("");
  const [workDoneDescription, setWorkDoneDescription] = useState("");
  // SESSION 45 GAP-39 — same progress tracking as the Quote panel.
  // Invoice submission has 4 line fields + 3 WO Completion Details
  // (repair category, repair code, work done description), so the
  // meter reads `N of (4 * lines + 3) fields copied · M of L lines
  // complete · X%`. Wording matches mockup 22:
  // `10 of 15 fields copied · lines complete, WO Completion pending`.
  const [copiedFields, setCopiedFields] = useState<Set<string>>(new Set());
  const [pdfAttached, setPdfAttached] = useState(false);
  // A14.2 (invoice parallel) — in-panel invoice-PDF download, same fetch
  // the invoice Overview tab uses (getInvoicePDF → res.result URL → anchor
  // download). Lifted here so staff grab the invoice PDF for the Corrigo
  // upload without leaving the panel. Same endpoint, not a second path.
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [photosAttached, setPhotosAttached] = useState(false);
  const [note, setNote] = useState("");
  const [marking, setMarking] = useState(false);
  // Session 65 item 8: gate the debounced progress save so it does not
  // fire while we are HYDRATING the checkboxes from the server on load
  // (which would immediately re-save the just-loaded state). Flips true
  // only after the first hydrate completes for this invoice.
  const [progressHydrated, setProgressHydrated] = useState(false);

  const load = async () => {
    setLoading(true);
    setProgressHydrated(false);
    const res = await getCorrigoExport({ data: { id: invoice.id } });
    setExportData(res);
    setRepairCategory(res?.corrigoRepairCategoryDefault ?? "");
    setRepairCode(res?.corrigoRepairCodeDefault ?? "");
    setWorkDoneDescription(res?.workDoneDescriptionDraft ?? "");
    // Session 65 item 8: hydrate the pre-posting checklist (which fields
    // were copied, pdf/photos attached) from the saved progress so it
    // survives a refresh. Best-effort - a miss just leaves the panel
    // starting empty as before.
    try {
      const prog = await getCorrigoProgress({ data: { id: invoice.id } });
      const p = prog?.progress;
      if (p) {
        if (Array.isArray(p.copiedFields)) setCopiedFields(new Set(p.copiedFields));
        if (typeof p.pdfAttached === "boolean") setPdfAttached(p.pdfAttached);
        if (typeof p.photosAttached === "boolean") setPhotosAttached(p.photosAttached);
      }
    } catch {
      // ignore - progress is a convenience, not required
    }
    setProgressHydrated(true);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [invoice?.id]);

  // Session 65 item 8: persist the checklist progress (debounced) whenever
  // it changes, once hydration is done. Last-write-wins server-side on
  // (company, invoice). UI-only; drives no invoice state.
  useEffect(() => {
    if (!progressHydrated || !invoice?.id) return;
    const t = setTimeout(() => {
      void saveCorrigoProgress({
        data: {
          id: invoice.id,
          progress: {
            copiedFields: Array.from(copiedFields),
            pdfAttached,
            photosAttached,
          },
        },
      });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [copiedFields, pdfAttached, photosAttached, progressHydrated, invoice?.id]);

  const copyField = async (value: string | number, label: string, fieldKey?: string) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.add(`${label} copied.`, "var(--theme-ui-colors-success)");
      // SESSION 45 GAP-39 — track copied fields for the header meter.
      if (fieldKey) {
        setCopiedFields((prev) => {
          const next = new Set(prev);
          next.add(fieldKey);
          return next;
        });
      }
    } catch {
      toast.add("Clipboard blocked.", "var(--theme-ui-colors-danger)");
    }
  };

  // A14.2 (invoice parallel) — mirrors invoice Overview's
  // handleDownloadInvoice exactly (getInvoicePDF → res.result URL → anchor
  // download, filename GMI_<seq>_invoice.pdf), so there is one PDF path,
  // not two. Lives here so the operator downloads the invoice PDF for the
  // Corrigo upload at the attach step instead of routing back to Overview.
  const downloadInvoicePdf = async () => {
    setPdfDownloading(true);
    try {
      const res = await getInvoicePDF({ data: { id: invoice.id } });
      if (!res?.result) {
        toast.add("Could not generate the invoice PDF.", "var(--theme-ui-colors-danger)");
        return;
      }
      const link = document.createElement("a");
      link.href = res.result;
      link.download = `GMI_${String(invoice.sequence).padStart(5, "0")}_invoice.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.add("Could not generate the invoice PDF.", "var(--theme-ui-colors-danger)");
    }
    setPdfDownloading(false);
  };

  const submitMarkPosted = async () => {
    setMarking(true);
    const res = await markPostedToCorrigo({ data: { id: invoice.id }, note: note.trim() || undefined });
    setMarking(false);
    if (res?.success) {
      toast.add("Invoice marked posted to Corrigo.", "var(--theme-ui-colors-success)");
      load();
    } else {
      toast.add(res?.message || "Error marking posted.", "var(--theme-ui-colors-danger)");
    }
  };

  if (loading) return null;
  if (!exportData?.lines?.length) return null;

  // Progress numbers. Per-line copy targets reduced to Qty + Rate (in
  // CorrigoPro the Category/Item is a pre-populated dropdown and our
  // Description is the item-name echo of it - neither is pasted), plus the
  // 3 WO Completion fields overall. Discount (when present) is optional
  // and excluded from the meter.
  const FIELDS_PER_LINE = 2;
  const WO_COMPLETION_FIELDS = 3; // Repair Category, Repair Code, Work Done Description
  const lineCount = exportData.lines.length;
  const totalLineFields = lineCount * FIELDS_PER_LINE;
  const totalFields = totalLineFields + WO_COMPLETION_FIELDS;
  const linesComplete = exportData.lines.filter((_: any, idx: number) => {
    return (
      copiedFields.has(`${idx}:qty`) &&
      copiedFields.has(`${idx}:rate`)
    );
  }).length;
  const woCompletionCopied =
    (copiedFields.has("wo:repairCategory") ? 1 : 0) +
    (copiedFields.has("wo:repairCode") ? 1 : 0) +
    (copiedFields.has("wo:workDoneDescription") ? 1 : 0);
  // Count only fields that count toward totalFields (line qty/rate + the 3
  // WO fields); the optional "discount" key is excluded so it can't push
  // the meter past 100%.
  const copiedCount = Array.from(copiedFields).filter((k) => k !== "discount").length;
  const progressPct = totalFields === 0 ? 0 : Math.round((copiedCount / totalFields) * 100);
  const allLinesComplete = linesComplete === lineCount && lineCount > 0;
  const allWoCompletionCopied = woCompletionCopied === WO_COMPLETION_FIELDS;
  const allComplete = allLinesComplete && allWoCompletionCopied;

  // Active line = the first line whose Qty + Rate are not both copied, so
  // the shared line card can draw the same dark-border "next up" highlight
  // the quote panel uses (mockup 08). -1 (none) once every line is done.
  const activeLineIdx = exportData.lines.findIndex(
    (_: any, idx: number) =>
      !(
        copiedFields.has(`${idx}:qty`) &&
        copiedFields.has(`${idx}:rate`)
      ),
  );
  const statusPhrase = allComplete
    ? "all fields copied"
    : allLinesComplete
    ? "lines complete, WO Completion pending"
    : allWoCompletionCopied
    ? "WO Completion done, lines pending"
    : "in progress";

  const posted = exportData.posted;

  return (
    <Box
      sx={{
        marginTop: "24px",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        background: "white",
      }}
    >
      <Box sx={{ padding: "16px 20px", background: "#FAFAF8", borderBottom: "1px solid", borderColor: "inputOutline" }}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"}>
              Corrigo Invoice Submission
            </H5>
            <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
              {exportData.planName ? `${exportData.planName} · ` : ""}
              {exportData.workOrderExternalId ? `WO ${exportData.workOrderExternalId} · ` : ""}
              Copy each field into CorrigoPro&apos;s Invoice form.
            </Subtext>
          </Box>
          {/* SESSION 45 GAP-39 — progress meter, mockup 22 wording.
           * Invoice submission has more fields than the quote export
           * (line fields + three WO Completion fields), so the status
           * phrase differentiates "lines complete, WO Completion
           * pending" from "WO Completion done, lines pending" — either
           * partial state is a real work sequence. */}
          <Box sx={{ minWidth: "200px", maxWidth: "280px" }}>
            <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "4px" }}>
              <Subtext fontSize={"10px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
                PROGRESS
              </Subtext>
              <Span fontFamily={"Poppins"} fontSize={"12px"} fontWeight={600} color={allComplete ? mnt.green : themevals.colors.black}>
                {progressPct}%
              </Span>
            </Flex>
            <Box sx={{ height: "6px", background: mnt.bgBeige, borderRadius: "3px", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${progressPct}%`,
                  // Always green at every fill level, matching the Quote
                  // panel (was blue #5B8AC0 in progress). Green = mnt.green.
                  background: mnt.green,
                  transition: "width 180ms ease",
                }}
              />
            </Box>
            <Subtext fontSize={"10px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} color={themevals.colors.dkgrey}>
              {copiedCount} of {totalFields} fields copied · {statusPhrase}
            </Subtext>
          </Box>
        </Flex>
      </Box>

      <Box sx={{ padding: "16px 20px" }}>
        {/* Invoice lines — shared line-card + copy-field treatment with
            the Quote panel (components/CorrigoPasteField). Same four
            fields (Category / Description / Qty / Rate). */}
        {exportData.lines.map((line: any, idx: number) => {
          // Only Qty + Rate are copy targets (Category is a pre-populated
          // CorrigoPro dropdown; our Description is the item-name echo of
          // that category). The category still shows as the line LABEL.
          const perLineCount =
            (copiedFields.has(`${idx}:qty`) ? 1 : 0) +
            (copiedFields.has(`${idx}:rate`) ? 1 : 0);
          const rateStr = line.rate.toLocaleString("en-US", { style: "currency", currency: "USD" });
          return (
            <CorrigoLineCard
              key={idx}
              lineNumber={idx + 1}
              description={line.category || line.description}
              copiedCount={perLineCount}
              fieldsPerLine={FIELDS_PER_LINE}
              active={idx === activeLineIdx}
              last={false}
            >
              <CorrigoField
                label="Qty"
                value={line.qty}
                onCopy={() => copyField(line.qty, "Qty", `${idx}:qty`)}
                copied={copiedFields.has(`${idx}:qty`)}
                wide
              />
              <CorrigoField
                label="Rate"
                value={rateStr}
                onCopy={() => copyField(line.rate, "Rate", `${idx}:rate`)}
                copied={copiedFields.has(`${idx}:rate`)}
                wide
              />
            </CorrigoLineCard>
          );
        })}

        {/* Discount (same model as the quote). Only shown when a discount
            was applied to this invoice - a single dollar figure to paste
            into CorrigoPro. Hidden when there's nothing to copy. */}
        {exportData.discountAmount != null && exportData.discountAmount > 0 && (
          <Box sx={{ marginTop: "10px" }}>
            <CorrigoField
              label="Discount"
              value={Number(exportData.discountAmount).toLocaleString("en-US", { style: "currency", currency: "USD" })}
              onCopy={() => copyField(exportData.discountAmount, "Discount", "discount")}
              copied={copiedFields.has("discount")}
              wide
            />
          </Box>
        )}

        {/* WO Completion Details — new for the Invoice flow (mockup 22) */}
        <Box sx={{ marginTop: "10px", border: "1px solid", borderColor: "inputOutline", borderRadius: "6px", padding: "12px 14px" }}>
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
            WO COMPLETION DETAILS
          </Subtext>
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"2px"}>
            Editable before copying — pre-filled from plan defaults and project completion.
          </Subtext>

          <Box sx={{ marginTop: "10px" }}>
            <EditableCorrigoField
              label="Repair Category"
              value={repairCategory}
              onChange={setRepairCategory}
              onCopy={() => copyField(repairCategory, "Repair Category", "wo:repairCategory")}
              copied={copiedFields.has("wo:repairCategory")}
            />
          </Box>
          <Box sx={{ marginTop: "10px" }}>
            <EditableCorrigoField
              label="Repair Code"
              value={repairCode}
              onChange={setRepairCode}
              onCopy={() => copyField(repairCode, "Repair Code", "wo:repairCode")}
              copied={copiedFields.has("wo:repairCode")}
            />
          </Box>
          <Box sx={{ marginTop: "10px" }}>
            <EditableCorrigoField
              label="Work Done Description"
              value={workDoneDescription}
              onChange={setWorkDoneDescription}
              onCopy={() => copyField(workDoneDescription, "Work Done Description", "wo:workDoneDescription")}
              copied={copiedFields.has("wo:workDoneDescription")}
              multiline
            />
          </Box>
        </Box>

        {/* Attachments — reminders only, mirrors the Quote panel's PDF step */}
        <Box sx={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid", borderColor: "inputOutline" }}>
          <Checkbox
            label="Attached the invoice PDF in Corrigo (Type: Document)"
            checked={pdfAttached}
            onChange={() => setPdfAttached(!pdfAttached)}
            labelSize="13px"
          />
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"} marginBottom={"10px"}>
            Download the invoice PDF here, then attach it in Corrigo for the FM&apos;s records.
          </Subtext>
          <BasicButton
            type="button"
            outline
            scheme="secondary"
            onClick={downloadInvoicePdf}
            $submitting={pdfDownloading}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor={themevals.colors.primary}
            keepText={true}
            styles={{ margin: "0 0 10px 0" }}
          >
            {!pdfDownloading && <DownloadIcon fill={themevals.colors.dkgrey} width="14px" />}
            Download invoice PDF
          </BasicButton>
          <Checkbox
            label="Attached completion photos in Corrigo (Type: Picture)"
            checked={photosAttached}
            onChange={() => setPhotosAttached(!photosAttached)}
            labelSize="13px"
          />
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"4px"}>
            Bundle from the site&apos;s snapshot capture, or upload individually.
          </Subtext>
        </Box>

        {/* GAP-38 — cost reconciliation. What the provider actually
         * billed vs what we committed vs what we're about to post to
         * the client. Catches a client-invoice line that doesn't
         * reflect the real job cost before Mark posted fires. */}
        {exportData.costReconciliation && (
          <CostReconciliation data={exportData.costReconciliation} />
        )}

        {/* Mark posted — audit-only, no gating on the checkboxes above */}
        <Box sx={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid", borderColor: "inputOutline" }}>
          {posted ? (
            <Flex sx={{ alignItems: "center", gap: "8px" }}>
              <CheckMarkIcon fill={themevals.colors.success} width="16px" height="16px" />
              <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
                Marked posted to Corrigo on {new Date(posted.createdAt).toLocaleDateString()}
                {posted.note ? ` — "${posted.note}"` : ""}
              </Subtext>
            </Flex>
          ) : (
            <Box>
              <Box sx={{ marginBottom: "10px" }}>
                <TextInput
                  type="textarea"
                  name="note"
                  label="Note (optional)"
                  rows={2}
                  value={note}
                  onChange={(val: any) => setNote(val)}
                />
              </Box>
              <BasicButton
                type="button"
                onClick={submitMarkPosted}
                $submitting={marking}
                $iconHeight="14px"
                $iconWidth="14px"
                $loadingColor="#fff"
                keepText={true}
              >
                {!marking && <CheckMarkIcon fill={"#fff"} />}
                Mark posted
              </BasicButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function EditableCorrigoField({
  label,
  value,
  onChange,
  onCopy,
  copied,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  onCopy: () => void;
  copied?: boolean;
  multiline?: boolean;
}) {
  return (
    <Box sx={{ opacity: copied ? 0.7 : 1, transition: "opacity 150ms ease" }}>
      <Flex sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Subtext fontSize={"10px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
          {label.toUpperCase()}
        </Subtext>
        <CopyButton label={label} onCopy={onCopy} copied={copied} />
      </Flex>
      <Box sx={{ marginTop: "4px" }}>
        <TextInput
          type={multiline ? "textarea" : "text"}
          name={label}
          rows={multiline ? 2 : undefined}
          value={value}
          onChange={(val: any) => onChange(val)}
        />
      </Box>
    </Box>
  );
}

function CopyButton({ label, onCopy, copied }: { label: string; onCopy: () => void; copied?: boolean }) {
  return (
    <Box
      as="button"
      onClick={onCopy}
      sx={{
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        flexShrink: 0,
        fontFamily: "Poppins",
        fontSize: "11px",
        fontWeight: 500,
        color: copied ? "#27500A" : themevals.colors.dkgrey,
        "&:hover": { color: themevals.colors.black, textDecoration: "underline" },
      }}
      aria-label={copied ? `Re-copy ${label} (already copied)` : `Copy ${label}`}
    >
      {copied ? "✓ Copied" : "Copy"}
    </Box>
  );
}

/**
 * GAP-38 — cost reconciliation summary. Three numbers GetMulch actually
 * stores: what we committed to the provider (PO), what the provider
 * billed us (provider invoice, with variance computed on approval), and
 * what we're posting to the client here. The provider invoice has no
 * structured per-line actuals — those live in its PDF — so this is a
 * job-level reconciliation, not a per-line one. Amber when the provider
 * hasn't billed yet (nothing to reconcile against) or the recorded
 * variance is non-trivial.
 */
function CostReconciliation({ data }: { data: any }) {
  const money = (n: number | null) =>
    n == null
      ? "—"
      : n.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const notBilledYet = data.providerBilled == null;
  const variance = data.varianceAmount;
  const notableVariance =
    variance != null && Math.abs(Number(variance)) >= 0.01;
  const alert = notBilledYet || notableVariance;

  return (
    <Box
      sx={{
        marginTop: "16px",
        border: "1px solid",
        borderColor: alert ? "#E7CC7A" : "inputOutline",
        background: alert ? "#FFF8E1" : "#FAFAF8",
        borderRadius: "6px",
        padding: "12px 14px",
      }}
    >
      <Subtext
        fontSize={"11px"}
        fontFamily={"Poppins"}
        fontWeight={600}
        margin={0}
        color={alert ? "#7A5A00" : themevals.colors.dkgrey}
      >
        COST RECONCILIATION{data.providerName ? ` · ${data.providerName}` : ""}
      </Subtext>

      <Box sx={{ marginTop: "8px" }}>
        <ReconRow label="Provider billed (actual)" value={money(data.providerBilled)} emphasize />
        <ReconRow label="PO commitment" value={money(data.poCommitment)} />
        <ReconRow label="Posting to client" value={money(data.clientInvoiceTotal)} />
      </Box>

      <Box sx={{ marginTop: "6px", paddingTop: "6px", borderTop: "1px dashed", borderColor: alert ? "#E7CC7A" : "inputOutline" }}>
        {notBilledYet ? (
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color="#7A5A00">
            Provider invoice not received yet — no billed actual to reconcile against. Confirm the client amount against the PO before posting.
          </Subtext>
        ) : notableVariance ? (
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color="#7A5A00">
            Provider billed {Number(variance) > 0 ? "over" : "under"} PO by {money(Math.abs(Number(variance)))}
            {data.variancePct != null ? ` (${Math.abs(Number(data.variancePct))}%)` : ""}
            {data.providerInvoiceStatus ? ` · ${data.providerInvoiceStatus.replace(/_/g, " ")}` : ""}. Check the client amount reflects the real cost.
          </Subtext>
        ) : (
          <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
            Provider billed matches the PO commitment.
            {data.providerInvoiceStatus ? ` · ${data.providerInvoiceStatus.replace(/_/g, " ")}` : ""}
          </Subtext>
        )}
      </Box>
    </Box>
  );
}

function ReconRow({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <Flex sx={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px" }}>
      <Span fontFamily={"Poppins"} fontSize={"12px"} color={themevals.colors.dkgrey}>
        {label}
      </Span>
      <Span
        fontFamily={"Poppins"}
        fontSize={emphasize ? "14px" : "13px"}
        fontWeight={emphasize ? 600 : 500}
        color={themevals.colors.black}
      >
        {value}
      </Span>
    </Flex>
  );
}
